import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  adminListCvOrders,
  adminGetCvOrderDetail,
  adminResendCvReceipt,
  adminResendCvInvoice,
  adminRetryCvOrder,
  adminRefundCvOrder,
} from "@/lib/api/cv-admin.functions";
import {
  Mail,
  Loader2,
  RefreshCcw,
  Undo2,
  RotateCcw,
  Search,
  FileText,
  Receipt,
  Info,
} from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/cv-orders",
)({
  component: AdminCvOrdersPage,
});

const SITE = "https://nkadimengdannylcs.com";

function fmt(d?: string | null) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-ZA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return d;
  }
}

function statusBadge(s: string) {
  const norm = (s || "").toLowerCase();
  if (norm === "paid")
    return <Badge className="bg-emerald-600 text-white">paid</Badge>;
  if (norm === "refunded")
    return <Badge className="bg-orange-600 text-white">refunded</Badge>;
  if (norm === "failed" || norm === "cancelled")
    return <Badge variant="destructive">{norm}</Badge>;
  if (norm === "processing")
    return <Badge className="bg-blue-600 text-white">processing</Badge>;
  return <Badge variant="secondary">{norm || "pending"}</Badge>;
}

function AdminCvOrdersPage() {
  const list = useServerFn(adminListCvOrders);
  const detail = useServerFn(adminGetCvOrderDetail);
  const resendReceipt = useServerFn(adminResendCvReceipt);
  const resendInvoice = useServerFn(adminResendCvInvoice);
  const retry = useServerFn(adminRetryCvOrder);
  const refund = useServerFn(adminRefundCvOrder);

  const [status, setStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [detailRef, setDetailRef] = useState<string | null>(null);
  const [refundFor, setRefundFor] = useState<{
    reference: string;
    title: string;
  } | null>(null);
  const [refundReason, setRefundReason] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-cv-orders", status, search],
    queryFn: () => list({ data: { status: status as any, search } }),
  });
  const orders = (data?.orders ?? []) as any[];

  const { data: detailData, isLoading: detailLoading, refetch: refetchDetail } =
    useQuery({
      queryKey: ["admin-cv-order-detail", detailRef],
      queryFn: () => detail({ data: { reference: detailRef! } }),
      enabled: !!detailRef,
    });

  const totals = useMemo(() => {
    let revenue = 0;
    let paid = 0;
    let refunded = 0;
    for (const o of orders) {
      if (o.status === "paid") {
        revenue += Number(o.amount_zar);
        paid += 1;
      }
      if (o.status === "refunded") refunded += 1;
    }
    return { revenue, paid, refunded };
  }, [orders]);

  async function run(
    label: string,
    key: string,
    fn: () => Promise<any>,
    okMsg: string,
  ) {
    setBusy(key);
    try {
      const r = await fn();
      if (r?.ok ?? true) toast.success(okMsg);
      else toast.error(r?.reason || `${label} failed`);
    } catch (e: any) {
      toast.error(e?.message || `${label} failed`);
    } finally {
      setBusy(null);
      refetch();
      if (detailRef) refetchDetail();
    }
  }

  async function submitRefund() {
    if (!refundFor) return;
    if (refundReason.trim().length < 3) {
      toast.error("Please enter a refund reason");
      return;
    }
    setBusy(`refund:${refundFor.reference}`);
    try {
      const r: any = await refund({
        data: { reference: refundFor.reference, reason: refundReason.trim() },
      });
      toast.success(
        `Marked refunded. Revoked ${r?.revokedCredits ?? 0} unused credits.`,
      );
      setRefundFor(null);
      setRefundReason("");
      refetch();
    } catch (e: any) {
      toast.error(e?.message || "Refund failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">CV orders (admin)</h1>
          <p className="text-sm text-muted-foreground">
            View, resend documents, retry, or refund AI CV purchases. Every
            action is logged to <code>cv_order_audit</code>.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          <RefreshCcw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </header>

      <div className="grid gap-3 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground">
              Paid orders shown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{totals.paid}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground">
              Revenue shown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">
              R{totals.revenue.toFixed(2)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground">
              Refunded
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{totals.refunded}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="space-y-3">
          <CardTitle>Orders</CardTitle>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Reference, PF id or email…"
                className="h-8 w-72 pl-7 text-xs"
              />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-8 w-[160px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : orders.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No CV orders match the filters.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Created</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Buyer</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Receipt</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((o) => {
                    const tokenQs = (o as any).public_token
                      ? `&t=${encodeURIComponent((o as any).public_token)}`
                      : "";
                    const invoiceUrl = `${SITE}/api/public/receipt?ref=${encodeURIComponent(
                      o.reference,
                    )}${tokenQs}&doc=invoice`;
                    const receiptUrl = `${SITE}/api/public/receipt?ref=${encodeURIComponent(
                      o.reference,
                    )}${tokenQs}`;


                    return (
                      <TableRow key={o.id}>
                        <TableCell className="text-xs text-muted-foreground">
                          {fmt(o.created_at)}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          <button
                            type="button"
                            className="hover:underline"
                            onClick={() => setDetailRef(o.reference)}
                          >
                            {o.reference}
                          </button>
                          {o.paid_provider_ref ? (
                            <div className="text-[10px] text-muted-foreground">
                              PF #{o.paid_provider_ref}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-xs">
                          <div className="font-medium">
                            {o.buyer_name || "—"}
                          </div>
                          <div className="text-muted-foreground">
                            {o.buyer_email || "—"}
                          </div>
                        </TableCell>
                        <TableCell>{o.quantity}</TableCell>
                        <TableCell className="whitespace-nowrap font-medium">
                          R{Number(o.amount_zar).toFixed(2)}
                        </TableCell>
                        <TableCell>{statusBadge(o.status)}</TableCell>
                        <TableCell className="text-xs">
                          {o.receipt_sent_at ? (
                            <span className="text-emerald-700">
                              sent {fmt(o.receipt_sent_at)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDetailRef(o.reference)}
                            >
                              <Info className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              asChild
                              size="sm"
                              variant="outline"
                              title="Open invoice PDF"
                            >
                              <a href={invoiceUrl} target="_blank" rel="noopener noreferrer">
                                <FileText className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                            {o.status === "paid" ? (
                              <Button
                                asChild
                                size="sm"
                                variant="outline"
                                title="Open receipt PDF"
                              >
                                <a href={receiptUrl} target="_blank" rel="noopener noreferrer">
                                  <Receipt className="h-3.5 w-3.5" />
                                </a>
                              </Button>
                            ) : null}
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={busy === `inv:${o.reference}`}
                              onClick={() =>
                                run(
                                  "Resend invoice",
                                  `inv:${o.reference}`,
                                  () =>
                                    resendInvoice({
                                      data: { reference: o.reference },
                                    }),
                                  "Invoice email queued",
                                )
                              }
                            >
                              {busy === `inv:${o.reference}` ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Mail className="h-3.5 w-3.5" />
                              )}
                              <span className="ml-1">Invoice</span>
                            </Button>
                            {o.status === "paid" ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={busy === `rec:${o.reference}`}
                                onClick={() =>
                                  run(
                                    "Resend receipt",
                                    `rec:${o.reference}`,
                                    () =>
                                      resendReceipt({
                                        data: { reference: o.reference },
                                      }),
                                    "Receipt email queued",
                                  )
                                }
                              >
                                {busy === `rec:${o.reference}` ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Mail className="h-3.5 w-3.5" />
                                )}
                                <span className="ml-1">Receipt</span>
                              </Button>
                            ) : null}
                            {(o.status === "failed" ||
                              o.status === "cancelled") && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={busy === `rtr:${o.reference}`}
                                onClick={() =>
                                  run(
                                    "Retry",
                                    `rtr:${o.reference}`,
                                    () =>
                                      retry({
                                        data: { reference: o.reference },
                                      }),
                                    "Order reset to pending",
                                  )
                                }
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                                <span className="ml-1">Retry</span>
                              </Button>
                            )}
                            {o.status === "paid" ? (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() =>
                                  setRefundFor({
                                    reference: o.reference,
                                    title: `${o.quantity} × AI CV (R${Number(
                                      o.amount_zar,
                                    ).toFixed(2)})`,
                                  })
                                }
                              >
                                <Undo2 className="h-3.5 w-3.5" />
                                <span className="ml-1">Refund</span>
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Refund dialog */}
      <Dialog
        open={!!refundFor}
        onOpenChange={(o) => (!o ? (setRefundFor(null), setRefundReason("")) : null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refund {refundFor?.reference}</DialogTitle>
            <DialogDescription>
              This marks the order refunded, revokes up to the purchased number
              of unused paid CV credits from the buyer's entitlement, and
              records the action in the audit trail.
              <br />
              <strong>You must still issue the cash refund manually in the
              PayFast merchant dashboard.</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              {refundFor?.title}
            </div>
            <Textarea
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Reason for refund (visible in audit log)"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => (setRefundFor(null), setRefundReason(""))}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitRefund}
              disabled={busy?.startsWith("refund:")}
            >
              {busy?.startsWith("refund:") ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Undo2 className="mr-2 h-4 w-4" />
              )}
              Confirm refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog
        open={!!detailRef}
        onOpenChange={(o) => (!o ? setDetailRef(null) : null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm">{detailRef}</DialogTitle>
            <DialogDescription>
              Order, ITN, email log and audit trail.
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center gap-2 py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-5 text-xs">
              <section className="rounded border bg-muted/30 p-3">
                <pre className="overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify((detailData as any)?.order, null, 2)}
                </pre>
              </section>

              <section>
                <h3 className="mb-2 font-semibold uppercase text-muted-foreground">
                  PayFast ITN events
                </h3>
                {(detailData as any)?.events?.length ? (
                  <ul className="space-y-1">
                    {(detailData as any).events.map((e: any) => (
                      <li
                        key={e.id}
                        className="flex flex-wrap items-center gap-2 rounded border bg-muted/40 px-2 py-1"
                      >
                        <Badge
                          variant={e.processed ? "default" : "destructive"}
                          className="text-[10px]"
                        >
                          {e.event_type}
                        </Badge>
                        <span className="text-muted-foreground">
                          {fmt(e.created_at)}
                        </span>
                        {e.processed_at ? (
                          <span className="text-emerald-700">
                            processed {fmt(e.processed_at)}
                          </span>
                        ) : null}
                        {e.error_message ? (
                          <span className="text-red-700">
                            {e.error_message}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">
                    No PayFast webhook events recorded.
                  </p>
                )}
              </section>

              <section>
                <h3 className="mb-2 font-semibold uppercase text-muted-foreground">
                  Email log
                </h3>
                {(detailData as any)?.emails?.length ? (
                  <ul className="space-y-1">
                    {(detailData as any).emails.map((m: any, i: number) => (
                      <li
                        key={i}
                        className="flex flex-wrap items-center gap-2 rounded border bg-muted/40 px-2 py-1"
                      >
                        <Badge variant="outline" className="text-[10px]">
                          {m.template_name}
                        </Badge>
                        <span>{m.recipient_email}</span>
                        <Badge
                          className={`text-[10px] text-white ${
                            m.status === "sent"
                              ? "bg-emerald-600"
                              : m.status === "failed"
                                ? "bg-red-600"
                                : "bg-slate-500"
                          }`}
                        >
                          {m.status}
                        </Badge>
                        <span className="text-muted-foreground">
                          {fmt(m.created_at)}
                        </span>
                        {m.error_message ? (
                          <span className="text-red-700">
                            {m.error_message}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">No emails logged.</p>
                )}
              </section>

              <section>
                <h3 className="mb-2 font-semibold uppercase text-muted-foreground">
                  Audit trail
                </h3>
                {(detailData as any)?.audit?.length ? (
                  <ul className="space-y-1">
                    {(detailData as any).audit.map((a: any) => (
                      <li
                        key={a.id}
                        className="rounded border bg-muted/40 px-2 py-1"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">
                            {a.action}
                          </Badge>
                          <span className="text-muted-foreground">
                            {fmt(a.created_at)}
                          </span>
                          {a.actor_email ? (
                            <span>{a.actor_email}</span>
                          ) : null}
                        </div>
                        {a.details ? (
                          <pre className="mt-1 overflow-x-auto whitespace-pre-wrap text-[11px]">
                            {JSON.stringify(a.details, null, 2)}
                          </pre>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">No audit entries.</p>
                )}
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
